import { useCallback, useEffect, useState } from "react";
import { Banknote, HeartHandshake, Loader2, Send, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Chip, DecisionChip, Kpi, Loading, PageHeader, Panel, SeverityChip } from "@/components/common/Chrome";
import { api, apiError, decisionPayload } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, shortDate } from "@/lib/format";

export default function Grants() {
  const { can } = useAuth();
  const [grants, setGrants] = useState(null);
  const [active, setActive] = useState(null);
  const [amount, setAmount] = useState(50000);
  const [purpose, setPurpose] = useState("health_programme");
  const [busy, setBusy] = useState(false);
  const [decision, setDecision] = useState(null);

  const load = useCallback(() => api.get("/charity/grants").then((r) => setGrants(r.data)), []);
  useEffect(() => { load().catch(() => setGrants([])); }, [load]);

  const disburse = async () => {
    setBusy(true);
    setDecision(null);
    try {
      const { data } = await api.post(`/charity/grants/${active.id}/disburse`, {
        amount, currency: active.currency, purpose_code: purpose,
      });
      setDecision(data.decision);
      toast.success(`Disbursement recorded · decision ${data.decision.decision}`);
      await load();
      setActive(null);
    } catch (e) {
      const dec = decisionPayload(e);
      if (dec) {
        setDecision(dec);
        toast.error("Disbursement blocked by the engine");
      } else {
        toast.error(apiError(e));
      }
    }
    setBusy(false);
  };

  if (!grants) return <Loading label="Loading grant agreements" />;
  const received = grants.filter((g) => g.direction === "received");
  const made = grants.filter((g) => g.direction === "made");

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="SCR-011 · Grant Workspace"
        title="Funder conditions, met on time"
        description="Agreements with budget lines, milestones and reporting obligations. Disbursements validate against terms, remaining budget, due diligence and sanctions screening before any value moves."
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
        <Kpi testId="kpi-grants-total" label="Agreements" value={grants.length} accent="sky" icon={HeartHandshake} />
        <Kpi testId="kpi-grants-received" label="Grants received" value={received.length} accent="emerald" />
        <Kpi testId="kpi-grants-made" label="Sub-grants made" value={made.length} accent="violet" />
        <Kpi testId="kpi-grants-dd" label="Due diligence complete" accent="amber" icon={ShieldCheck}
             value={`${grants.filter((g) => g.due_diligence_complete).length}/${grants.length}`} />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        {grants.map((g) => {
          const remaining = (g.amount || 0) - (g.disbursed || 0);
          return (
            <Panel key={g.id} testId={`grant-card-${g.reference}`}
                   kicker={`${g.reference} · ${g.direction}`} title={g.title}
                   actions={
                     <Chip className={g.due_diligence_complete
                       ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                       : "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300"}>
                       {g.due_diligence_complete ? "DD complete" : "DD outstanding"}
                     </Chip>
                   }>
              <div className="space-y-4 p-4">
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <Line k="Counterparty" v={`${g.counterparty} (${g.counterparty_country})`} />
                  <Line k="Value" v={money(g.amount, g.currency)} />
                  <Line k="Disbursed" v={money(g.disbursed, g.currency)} />
                  <Line k="Remaining" v={money(remaining, g.currency)} />
                  <Line k="Period" v={`${shortDate(g.start_date)} → ${shortDate(g.end_date)}`} />
                  <Line k="Status" v={g.status} />
                </div>

                <Progress value={Math.min(((g.disbursed || 0) / (g.amount || 1)) * 100, 100)} className="h-1.5 bg-muted" />

                <div>
                  <div className="kicker mb-1.5">Budget lines</div>
                  <div className="flex flex-wrap gap-1.5">
                    {(g.budget_lines || []).map((b) => (
                      <span key={b.line} className="rounded border border-border px-2 py-1 text-[10px] text-foreground/80">
                        {b.line} · <span className="font-mono text-muted-foreground">{money(b.amount, g.currency, true)}</span>
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="kicker mb-1.5">Milestones &amp; reporting</div>
                  <div className="space-y-1">
                    {(g.milestones || []).map((m) => (
                      <div key={m.name} className="flex items-center justify-between text-[11px]">
                        <span className="text-foreground/80">{m.name}</span>
                        <span className="flex items-center gap-2 font-mono text-[10px] text-muted-foreground">
                          {shortDate(m.due)}
                          <Chip className={m.status === "complete"
                            ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                            : "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300"}>{m.status}</Chip>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {can("grant", "write") && (
                  <Button size="sm" onClick={() => { setActive(g); setAmount(Math.min(50000, remaining)); setDecision(null); }}
                          data-testid={`disburse-button-${g.reference}`}
                          className="rounded-full bg-primary text-xs text-white hover:bg-primary/90">
                    <Send className="mr-1.5 h-3 w-3" /> Request disbursement
                  </Button>
                )}
              </div>
            </Panel>
          );
        })}
      </div>

      <Dialog open={Boolean(active)} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent className="max-w-md border-border bg-card">
          <DialogHeader>
            <DialogTitle>Disburse against {active?.reference}</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Validated against agreement terms, remaining budget, due diligence currency and sanctions screening (FR-GRT-002).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-[11px] text-muted-foreground">Amount ({active?.currency})</Label>
              <Input type="number" value={amount} onChange={(e) => setAmount(Number(e.target.value))}
                     data-testid="disburse-amount-input" className="bg-muted/40 font-mono" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] text-muted-foreground">Purpose code</Label>
              <Input value={purpose} onChange={(e) => setPurpose(e.target.value)}
                     data-testid="disburse-purpose-input" className="bg-muted/40 font-mono text-xs" />
            </div>
            {decision && (
              <div className="rounded border border-border bg-muted/40 p-3" data-testid="disburse-decision-banner">
                <DecisionChip decision={decision.decision} />
                <div className="mt-2 space-y-1">
                  {decision.triggered.map((t) => (
                    <div key={t.rule_key} className="flex items-start gap-2 text-[11px]">
                      <SeverityChip severity={t.effective_severity} />
                      <span className="font-mono text-primary">{t.rule_key}</span>
                      <span className="text-muted-foreground">{t.title}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <Button onClick={disburse} disabled={busy} data-testid="disburse-confirm-button"
                    className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Banknote className="mr-2 h-4 w-4" />}
              Confirm disbursement
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Line = ({ k, v }) => (
  <div>
    <div className="kicker">{k}</div>
    <div className="mt-0.5 text-foreground/80">{v}</div>
  </div>
);
